import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { auth } from '../firebase'; // Ensure you have the correct path to your firebase config
import { createUserWithEmailAndPassword, sendEmailVerification, signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { useAuth } from '../context/AuthContext'; // Import your AuthContext
import { Alert, AlertDescription } from "../components/ui/alert"; // Import Alert components

export default function Signup() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [name, setName] = useState('');
    const [error, setError] = useState(''); // State to hold error messages
    const [loading, setLoading] = useState(false); // Loading state
    const [verificationSent, setVerificationSent] = useState(false);
    const { setCurrentUser, createUserInFirestore } = useAuth(); // Destructure setCurrentUser from context
    const navigate = useNavigate(); // Initialize useNavigate

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError(""); // Reset error state
        setLoading(true); // Set loading to true
        try {
            // Create user with email and password using Firebase
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;

            // Send verification email
            await sendEmailVerification(user);
            setVerificationSent(true);

            const response = await createUserInFirestore('email', user.uid, user.email, user.displayName, user.photoURL, user.emailVerified)

            setCurrentUser(response);
            console.log("User signed up:", user);

            // Navigate to profile page after a delay to show verification message
            setTimeout(() => {
                navigate('/profile');
            }, 3000);
        } catch (error) {
            console.error('Error signing up:', error);
            setError(error.message); // Set error message from Firebase
        } finally {
            setLoading(false); // Reset loading state
        }
    };

    const handleGoogleSignup = async () => {
        const provider = new GoogleAuthProvider();
        try {
            const result = await signInWithPopup(auth, provider);
            const user = result.user;

            // Call the backend to create/update user in Firestore
            const response = await createUserInFirestore('google', user.uid, user.email, user.displayName, user.photoURL, user.emailVerified)
            setCurrentUser(response)

            navigate('/scraper');
        } catch (error) {
            console.error("Error signing up with Google:", error);
            setError(error.message);
        }
    };

    return (
        <Card className="w-full max-w-sm mx-auto border-none shadow-none">
            <CardHeader className="space-y-1">
                <CardTitle className="text-2xl">Create Account</CardTitle>
                <CardDescription>Sign up for a new account</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}

                {verificationSent && (
                    <Alert className="bg-green-50 border-green-200">
                        <AlertDescription className="text-green-800">
                            Verification email sent! Please check your inbox and verify your email.
                        </AlertDescription>
                    </Alert>
                )}

                <div className="space-y-3">
                    <Button variant="outline" className="w-full" onClick={handleGoogleSignup}>
                        <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                        </svg>
                        Continue with Google
                    </Button>
                </div>

                <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-background px-2 text-muted-foreground">or</span>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium">Name</label>
                        <Input type="text" placeholder="Your Name" value={name} onChange={(e) => setName(e.target.value)} />
                    </div>
                    <div>
                        <label className="block text-sm font-medium">Email</label>
                        <Input type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
                    </div>
                    <div>
                        <label className="block text-sm font-medium">Password</label>
                        <Input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
                    </div>
                    <Button type="submit" className="w-full" disabled={loading}>
                        {loading ? "Creating Account..." : "Sign Up"}
                    </Button>
                </form>
                <div className="text-center text-sm">
                    {"Already have an account? "}
                    <Link to="/login" className="text-primary font-bold underline hover:text-primary/80 transition-colors">
                        Login Now
                    </Link>
                </div>
            </CardContent>
        </Card>
    );
} 